import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { DOCUMENT } from '../constants/documentTheme';
import {
  LIVE_STATUS_LABEL_KEYS,
  LIVE_STATUS_TEXT_COLORS,
  fetchLiveFlightStatus,
  getScheduleFallbackStatus,
  isWithinLiveStatusWindow,
} from '../utils/liveFlightStatus';

export { fetchLiveFlightStatus } from '../utils/liveFlightStatus';

const STATUS_COLORS = {
  scheduled: LIVE_STATUS_TEXT_COLORS.success,
  active: LIVE_STATUS_TEXT_COLORS.success,
  in_air: LIVE_STATUS_TEXT_COLORS.success,
  landed: LIVE_STATUS_TEXT_COLORS.success,
  cancelled: LIVE_STATUS_TEXT_COLORS.error,
  delayed: LIVE_STATUS_TEXT_COLORS.warning,
  diverted: LIVE_STATUS_TEXT_COLORS.error,
  incident: LIVE_STATUS_TEXT_COLORS.error,
};

const ALERT_STATUSES = new Set(['cancelled', 'delayed', 'diverted', 'incident']);

const formatClock = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'HH:mm');
};

const DetailRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        py: 0.75,
        borderTop: `1px solid ${DOCUMENT.rule}`,
      }}
    >
      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    </Box>
  );
};

/**
 * Clickable flight code + live status for one itinerary leg.
 * compact: colored flight code only; alert words (delayed/cancelled) stay inline.
 */
const FlightLegStatus = ({
  airline = '',
  flightNumber = '',
  displayNumber = null,
  departureTime = null,
  departureAirport = null,
  arrivalAirport = null,
  arrivalTime = null,
  compact = false,
  enabled = true,
}) => {
  const { t } = useTranslation();
  const [statusPayload, setStatusPayload] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [open, setOpen] = useState(false);

  const label = displayNumber
    || [airline, flightNumber].filter(Boolean).join(' ').trim()
    || String(flightNumber || '').trim();

  const canLookup = Boolean(enabled && label);
  const withinLiveWindow = isWithinLiveStatusWindow(departureTime);

  // Default to booked itinerary times — no provider calls on mount.
  useEffect(() => {
    if (!canLookup) {
      setLoading(false);
      setStatusPayload(null);
      return;
    }
    const schedule = getScheduleFallbackStatus(departureTime, arrivalTime);
    setStatusPayload({
      success: true,
      available: true,
      source: 'itinerary',
      flight_number: label,
      status: schedule.status,
      departure: {
        airport: departureAirport || null,
        scheduled: departureTime || null,
      },
      arrival: {
        airport: arrivalAirport || null,
        scheduled: arrivalTime || null,
      },
    });
    setLoading(false);
  }, [airline, flightNumber, label, departureTime, arrivalTime, departureAirport, arrivalAirport, canLookup]);

  const loadLiveStatus = useCallback(async (signal) => {
    if (!canLookup || !withinLiveWindow) return;
    setLoading(true);
    try {
      const payload = await fetchLiveFlightStatus({
        airline,
        flightNumber: flightNumber || label,
        departureDate: departureTime,
        arrivalTime,
        departureAirport,
        arrivalAirport,
        preferSchedule: false,
        signal,
      });
      setStatusPayload(payload);
    } catch (error) {
      if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
        setStatusPayload((prev) => prev || {
          success: false,
          error: 'lookup_failed',
          schedule: getScheduleFallbackStatus(departureTime, arrivalTime),
        });
      }
    } finally {
      setLoading(false);
    }
  }, [
    airline,
    flightNumber,
    label,
    departureTime,
    arrivalTime,
    departureAirport,
    arrivalAirport,
    canLookup,
    withinLiveWindow,
  ]);

  const isLive = Boolean(statusPayload?.success && (statusPayload.source === 'live' || statusPayload.source === 'aviationstack'));
  const isScheduleSource = Boolean(
    statusPayload?.success
    && ['duffel_schedule', 'itinerary', 'schedule'].includes(statusPayload.source)
  );
  const scheduleFallback = (!statusPayload?.success && !loading)
    ? (statusPayload?.schedule || getScheduleFallbackStatus(departureTime, arrivalTime))
    : null;
  const statusKey = (isLive || isScheduleSource)
    ? statusPayload.status
    : scheduleFallback?.status;
  const statusColor = STATUS_COLORS[statusKey] || (loading ? DOCUMENT.ink3 : DOCUMENT.ink2);
  const statusLabel = statusKey
    ? t(LIVE_STATUS_LABEL_KEYS[statusKey] || LIVE_STATUS_LABEL_KEYS.scheduled, {
      defaultValue: statusKey.replace(/_/g, ' '),
    })
    : null;
  const showAlertLabel = ALERT_STATUSES.has(statusKey);
  const showStatusWord = showAlertLabel || (compact && Boolean(statusLabel && !loading));
  const dialogStatusLabel = (isLive || isScheduleSource)
    ? statusLabel
    : (loading
      ? t('flightStatus.checking', { defaultValue: 'Checking…' })
      : statusLabel || t('flightStatus.unavailableShort', { defaultValue: 'No status' }));
  const detailPayload = (isLive || isScheduleSource) ? statusPayload : null;

  const openDialog = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
    // Live AviationStack only when the user opens status on a near-departure leg.
    if (withinLiveWindow) {
      const controller = new AbortController();
      loadLiveStatus(controller.signal);
    }
  }, [withinLiveWindow, loadLiveStatus]);

  const closeDialog = useCallback((event) => {
    event?.stopPropagation?.();
    setOpen(false);
  }, []);

  const routeLabel = useMemo(() => {
    const from = statusPayload?.departure?.airport || departureAirport;
    const to = statusPayload?.arrival?.airport || arrivalAirport;
    if (from && to) return t('flightStatus.route', { from, to, defaultValue: `${from} → ${to}` });
    return null;
  }, [statusPayload, departureAirport, arrivalAirport, t]);

  if (!label) return null;

  if (!enabled) {
    return (
      <Typography
        component="span"
        data-testid="flight-leg-status-badge"
        sx={{ fontSize: 12, color: DOCUMENT.ink2 }}
      >
        {label}
      </Typography>
    );
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={openDialog}
        data-testid="flight-leg-status"
        aria-label={t('flightStatus.viewStatus', { flight: label, defaultValue: `Status for ${label}` })}
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 0.5,
          p: 0,
          m: 0,
          border: 0,
          background: 'none',
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
          verticalAlign: 'baseline',
          '&:hover .flight-leg-code': {
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          },
        }}
      >
        <Typography
          component="span"
          className="flight-leg-code"
          data-testid="flight-leg-status-badge"
          sx={{
            fontSize: 12,
            fontWeight: statusKey && !loading ? 600 : 400,
            color: statusColor,
            transition: 'color 120ms ease',
          }}
        >
          {label}
        </Typography>
        {(showStatusWord || (!compact && statusLabel)) && statusLabel && (
          <Typography
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: showAlertLabel ? 600 : 500,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: statusColor,
            }}
          >
            {statusLabel}
          </Typography>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={closeDialog}
        onClick={(event) => event.stopPropagation()}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: `1px solid ${DOCUMENT.cardRule}`,
            bgcolor: DOCUMENT.surface,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            fontFamily: DOCUMENT.display,
            fontWeight: 400,
            pr: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: DOCUMENT.accent, mb: 0.5 }}>
              {t('flightStatus.title', { defaultValue: 'Flight status' })}
            </Typography>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22 }}>
              {statusPayload?.flight_number || label.replace(/\s+/g, '')}
            </Typography>
          </Box>
          <IconButton aria-label={t('common.close', { defaultValue: 'Close' })} onClick={closeDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2.5 }}>
          {statusPayload?.airline_name && (
            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 1 }}>
              {statusPayload.airline_name}
            </Typography>
          )}
          {routeLabel && (
            <Typography sx={{ fontSize: 14, mb: 1.5 }}>{routeLabel}</Typography>
          )}

          {loading && !statusPayload && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} sx={{ color: DOCUMENT.accent }} />
            </Box>
          )}

          {!loading && statusPayload && !statusPayload.success && (
            <Box sx={{ py: 1 }}>
              {statusLabel && (
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: statusColor, mb: 1 }}>
                  {statusLabel}
                </Typography>
              )}
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
                {t('flightStatus.notFound', { defaultValue: 'No live status for this flight yet.' })}
              </Typography>
              <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 1.25 }}>
                {t('flightStatus.fromItinerary', { defaultValue: 'From your itinerary' })}
              </Typography>
              {(departureTime || arrivalTime) && (
                <Box sx={{ mt: 1.5 }}>
                  <DetailRow
                    label={t('flightStatus.departure', { defaultValue: 'Departure' })}
                    value={formatClock(departureTime)}
                  />
                  <DetailRow
                    label={t('flightStatus.arrival', { defaultValue: 'Arrival' })}
                    value={formatClock(arrivalTime)}
                  />
                </Box>
              )}
            </Box>
          )}

          {detailPayload && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: statusColor,
                  }}
                >
                  {dialogStatusLabel}
                </Typography>
                <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3 }}>
                  {isLive
                    ? t('flightStatus.liveBadge', { defaultValue: 'Live' })
                    : t('flightStatus.scheduleBadge', { defaultValue: 'Schedule' })}
                </Typography>
                {Number(detailPayload.departure?.delay) >= 15 && (
                  <Typography sx={{ fontSize: 12, color: DOCUMENT.oxblood }}>
                    {t('flightStatus.delayMinutes', {
                      count: Number(detailPayload.departure.delay),
                      defaultValue: `${detailPayload.departure.delay} min delay`,
                    })}
                  </Typography>
                )}
              </Box>
              {isScheduleSource && (
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink2, mb: 1.5 }}>
                  {t('flightStatus.scheduleSource', {
                    defaultValue: 'Booked itinerary times. Live gate updates appear closer to departure.',
                  })}
                </Typography>
              )}

              <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.5 }}>
                {t('flightStatus.departure', { defaultValue: 'Departure' })}
              </Typography>
              <DetailRow
                label={t('flightStatus.scheduled', { defaultValue: 'Scheduled' })}
                value={formatClock(detailPayload.departure?.scheduled || departureTime)}
              />
              <DetailRow
                label={t('flightStatus.estimated', { defaultValue: 'Estimated' })}
                value={formatClock(detailPayload.departure?.estimated || detailPayload.departure?.actual)}
              />
              {detailPayload.departure?.terminal && (
                <DetailRow
                  label={t('itinerary.terminal', { defaultValue: 'Terminal' })}
                  value={detailPayload.departure.terminal}
                />
              )}
              {detailPayload.departure?.gate && (
                <DetailRow
                  label={t('itinerary.gate', { defaultValue: 'Gate' })}
                  value={detailPayload.departure.gate}
                />
              )}

              <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.ink3, mt: 1.5, mb: 0.5 }}>
                {t('flightStatus.arrival', { defaultValue: 'Arrival' })}
              </Typography>
              <DetailRow
                label={t('flightStatus.scheduled', { defaultValue: 'Scheduled' })}
                value={formatClock(detailPayload.arrival?.scheduled || arrivalTime)}
              />
              <DetailRow
                label={t('flightStatus.estimated', { defaultValue: 'Estimated' })}
                value={formatClock(detailPayload.arrival?.estimated || detailPayload.arrival?.actual)}
              />
              {detailPayload.arrival?.terminal && (
                <DetailRow
                  label={t('itinerary.terminal', { defaultValue: 'Terminal' })}
                  value={detailPayload.arrival.terminal}
                />
              )}
              {detailPayload.arrival?.gate && (
                <DetailRow
                  label={t('itinerary.gate', { defaultValue: 'Gate' })}
                  value={detailPayload.arrival.gate}
                />
              )}

              {detailPayload.last_updated && (
                <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3, mt: 2 }}>
                  {t('flightStatus.lastUpdated', {
                    time: formatClock(detailPayload.last_updated) || detailPayload.last_updated,
                    defaultValue: `Updated ${formatClock(detailPayload.last_updated) || ''}`,
                  })}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FlightLegStatus;
